import React, { useMemo, useRef, useState } from "react";
import { MenuItem, MenuItemType, SiteTheme } from "../../types/types";
import { datalayerPush } from "../../GTM";

interface MenuProps {
    menuItems: MenuItem[];
    siteTheme: SiteTheme;
}

const Menu: React.FC<MenuProps> = ({ menuItems, siteTheme }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [submenu, setSubmenu] = useState<MenuItem[] | null>(null);
    const [expandedSubItems, setExpandedSubItems] = useState<
        Record<string, boolean>
    >({});
    const menuRef = useRef(null);
    const buttonsWraperRef = useRef(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isAnimatingSubmenu, setIsAnimatingSubmenu] = useState(false);

    const handleMenuClick = (item: MenuItem) => {
        if (item.type === MenuItemType.SUBMENU) {
            setSubmenu(item.children || []);
            requestAnimationFrame(() => {
                setIsAnimatingSubmenu(true);
            });
            setExpandedSubItems({});
        } else {
            resetSubmenu();
        }
    };

    const toggleSubItemExpansion = (label: string) => {
        setExpandedSubItems((prev) => ({
            ...prev,
            [label]: !prev[label],
        }));
    };

    const resetSubmenu = (datalayerEvent?: string) => {
        if (datalayerEvent) datalayerPush(datalayerEvent);
        setSubmenu(null);
        setExpandedSubItems({});
        setIsAnimatingSubmenu(false);
    };

    const freezeBackground = () => {
        document.getElementsByTagName("body")[0].classList.add("noscroll");
    };

    const unfreezeBackground = () => {
        document.getElementsByTagName("body")[0].classList.remove("noscroll");
    };

    const openMenu = () => {
        datalayerPush("menu_click");
        setIsOpen(true);
        // Allow DOM to render before starting animation
        requestAnimationFrame(() => {
            setIsAnimating(true);
        });
        freezeBackground();
    };

    const closeMenu = (
        persistMenuStates: boolean,
        clickedCloseButton?: boolean,
    ) => {
        if (clickedCloseButton) datalayerPush("menu_click");
        setIsAnimating(false);

        setTimeout(() => {
            unfreezeBackground();
            if (persistMenuStates) {
                setIsOpen(false);
                return;
            } else {
                resetSubmenu();
                setIsOpen(false);
                return;
            }
        }, 300); // Match this with $menu-transition-duration
    };

    const getCloseImageSource = () => {
        if (siteTheme === SiteTheme.GARANT)
            return "/static/images/garant/cross/cross-green-thin.svg";
        if (siteTheme === SiteTheme.ELDORADO)
            return "/static/images/eldorado/cross/cross-red-thin.svg";
        if (siteTheme === SiteTheme.MINSTINGEN)
            return "/static/images/minstingen/cross/cross-purple-thin.svg";
    };

    const getExternalLinkImageSource = () => {
        if (siteTheme === SiteTheme.GARANT)
            return "/static/images/garant/icons/external-link-green.svg";
        return "/static/images/base/external-link.svg";
    };

    const getChevronImageSource = () => {
        if (siteTheme === SiteTheme.GARANT)
            return "/static/images/garant/icons/chevron-right-green.svg";
        return "/static/images/base/chevron-right.svg";
    };

    const getMenuImageSource = () => {
        if (siteTheme === SiteTheme.GARANT)
            return "/static/images/garant/icons/menu.svg";
        if (siteTheme === SiteTheme.ELDORADO)
            return "/static/images/eldorado/icons/menu.svg";
        if (siteTheme === SiteTheme.MINSTINGEN)
            return "/static/images/minstingen/icons/menu.svg";
    };

    const lastTagIndex = useMemo(() => {
        return submenu?.findLastIndex((item) => item.type === MenuItemType.TAG);
    }, [submenu]);

    return (
        <>
            {isOpen && (
                <div
                    className={`menu-backdrop ${isAnimating ? "backdrop-visible" : ""}`}
                    onClick={() => closeMenu(true)}
                ></div>
            )}

            {!isOpen && (
                <button
                    aria-label="Öppna meny"
                    aria-expanded={false}
                    className="burger"
                    title="Meny"
                    onClick={() => openMenu()}
                >
                    <img
                        alt=""
                        aria-hidden="true"
                        height="24"
                        src={getMenuImageSource()}
                        width="25"
                    />
                </button>
            )}

            <div
                ref={menuRef}
                className={`menu ${isOpen ? "visible" : ""} ${isAnimating ? "menu-open" : ""}`}
                inert={!isOpen}
            >
                <div
                    className={`menu-buttons-wrapper  ${submenu ? "" : "reversed"}`}
                    ref={buttonsWraperRef}
                >
                    {submenu && isOpen && (
                        <button
                            aria-label="Tillbaka till huvudmenyn"
                            className="menu-top-icon show-on-s back-icon"
                            onClick={() => resetSubmenu("menu_back")}
                        >
                            <img
                                aria-hidden="true"
                                src={getChevronImageSource()}
                            />
                        </button>
                    )}
                    <button
                        aria-label="Stäng meny"
                        aria-expanded={true}
                        className="menu-top-icon close-menu"
                        onClick={() => closeMenu(false, true)}
                        title="Stäng meny"
                    >
                        <img
                            alt=""
                            height="18"
                            src={getCloseImageSource()}
                            width="19"
                        />
                    </button>
                </div>

                <div className="menus-wrapper">
                    <div className={`main-menu ${submenu ? "hide-on-s" : ""}`}>
                        <ul>
                            {menuItems.map((item, index) => (
                                <li key={index}>
                                    {item.type === MenuItemType.EXTERNAL ? (
                                        <a
                                            className="external-link"
                                            href={item.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {item.label}
                                            <img
                                                aria-hidden="true"
                                                src={getExternalLinkImageSource()}
                                            />
                                            <span className="visuallyhidden">
                                                (Öppnas i ny flik)
                                            </span>
                                        </a>
                                    ) : item.type === MenuItemType.SUBMENU ? (
                                        <button
                                            aria-expanded={
                                                submenu === item.children
                                            }
                                            aria-label={`${item.label}, undermeny fälls ut till höger`}
                                            className={`expand-button ${item.active ? "active" : ""}`}
                                            onClick={() =>
                                                handleMenuClick(item)
                                            }
                                            title={`${item.label}, undermeny fälls ut till höger`}
                                        >
                                            <span>{item.label}</span>
                                            <img
                                                src={getChevronImageSource()}
                                                aria-hidden="true"
                                                height="16"
                                                width="11"
                                            />
                                        </button>
                                    ) : (
                                        <a
                                            className={
                                                item.active ? "active" : ""
                                            }
                                            data-gtm-click-type="menu"
                                            data-gtm-menu-level="1"
                                            href={item.url}
                                        >
                                            {item.label}
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div
                        aria-label="Undermeny"
                        className={`submenu ${submenu ? "visible" : ""} ${isAnimatingSubmenu ? "submenu-open" : ""}`}
                        inert={!submenu}
                    >
                        <ul>
                            {submenu?.map((subItem, subIndex) => (
                                <li
                                    key={subIndex}
                                    className={`submenu-list-item ${subIndex === lastTagIndex ? "last-tag-item" : ""}`}
                                >
                                    {subItem.type === MenuItemType.SUBMENU ? (
                                        <button
                                            aria-expanded={
                                                expandedSubItems[
                                                    subItem.label
                                                ] || false
                                            }
                                            aria-label={`${subItem.label}, undermeny fälls ut neråt`}
                                            className={`expand-button ${subItem.active ? "active" : ""}`}
                                            onClick={() =>
                                                toggleSubItemExpansion(
                                                    subItem.label,
                                                )
                                            }
                                            title={`${subItem.label}, undermeny fälls ut neråt`}
                                        >
                                            <span>{subItem.label}</span>
                                            <img
                                                aria-hidden="true"
                                                className={`${
                                                    expandedSubItems[
                                                        subItem.label
                                                    ]
                                                        ? "animate-up"
                                                        : "animate-down"
                                                }`}
                                                height="12"
                                                src={getChevronImageSource()}
                                                width="8"
                                            />
                                        </button>
                                    ) : (
                                        <a
                                            className={`${subItem.type === MenuItemType.TAG ? "tag" : "main-page-header"} ${subItem.active ? "active" : ""}`}
                                            data-gtm-click-type="menu"
                                            data-gtm-menu-level="2"
                                            href={subItem.url}
                                        >
                                            <span>{subItem.label}</span>
                                        </a>
                                    )}

                                    {expandedSubItems[subItem.label] &&
                                        subItem.children && (
                                            <ul className="submenu-item-children">
                                                {subItem.children.map(
                                                    (child, childIndex) => (
                                                        <li key={childIndex}>
                                                            <a
                                                                className={
                                                                    child.active
                                                                        ? "active"
                                                                        : ""
                                                                }
                                                                data-gtm-click-type="menu"
                                                                data-gtm-menu-level="3"
                                                                href={child.url}
                                                            >
                                                                {child.label}
                                                            </a>
                                                        </li>
                                                    ),
                                                )}
                                            </ul>
                                        )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Menu;
